import { Stack } from "@fluentui/react";
import SyntaxHighlighter from "react-syntax-highlighter";
import styles from "./AnalysisPanel.module.css";
import { Thoughts, TokenUsage } from "../../api";
import { ProductResultCard } from "./ProductResultCard"; // ensure correct path
import { PromptSection, PromptMessage } from "./PromptSection";
import { TokenUsageCard } from "./TokenUsageCard";

interface Props {
    thoughts: Thoughts[];
    usage?: TokenUsage; // Added
}

// Check if item looks like a product
const isProductFormat = (item: any): boolean => {
    return (
        item &&
        typeof item === "object" &&
        "id" in item &&
        "title" in item &&
        "brand" in item &&
        "price" in item &&
        "image_url" in item &&
        "description" in item
    );
};

// Parse a JSON-encoded prompt message
const parsePromptMessageLocal = (messageStr: string): PromptMessage | null => {
    try {
        const parsed = JSON.parse(messageStr);
        if (parsed && typeof parsed === "object" && typeof parsed.role === "string" && typeof parsed.content === "string") {
            return { role: parsed.role, content: parsed.content };
        }
    } catch (e) {
        console.error("❌ Failed to parse prompt JSON:", messageStr, e);
    }
    return null;
};

export const ThoughtProcess = ({ thoughts, usage }: Props) => {
    return (
        <>
            <ul className={styles.tList}>
                {thoughts.map((t, ind) => (
                    <li className={styles.tListItem} key={ind}>
                        <div className={styles.tStep}>{t.title}</div>

                        {Array.isArray(t.description) ? (
                            (() => {
                                const firstMessage = t.description[0];

                                // Handle both JSON strings and direct objects
                                let parsedMessages: PromptMessage[] = [];

                                if (typeof firstMessage === "string") {
                                    // Try to parse as JSON strings (existing logic)
                                    const parsedFirst = parsePromptMessageLocal(firstMessage);
                                    if (parsedFirst) {
                                        parsedMessages = t.description
                                            .map(msg => (typeof msg === "string" ? parsePromptMessageLocal(msg) : null))
                                            .filter((m): m is PromptMessage => m !== null);
                                    }
                                } else if (typeof firstMessage === "object" && firstMessage !== null && "role" in firstMessage && "content" in firstMessage) {
                                    // Handle direct message objects (for intent parsing prompt)
                                    parsedMessages = t.description.filter(
                                        (msg): msg is PromptMessage => typeof msg === "object" && msg !== null && "role" in msg && "content" in msg
                                    );
                                }

                                if (parsedMessages.length > 0) {
                                    return <PromptSection messages={parsedMessages} />;
                                }

                                if (isProductFormat(firstMessage)) {
                                    return (
                                        <div>
                                            {t.description.map((product: any, idx: number) => (
                                                <ProductResultCard key={product.id || idx} product={product} />
                                            ))}
                                        </div>
                                    );
                                }

                                return (
                                    <SyntaxHighlighter language="json" wrapLongLines className={styles.tCodeBlock}>
                                        {JSON.stringify(t.description, null, 2)}
                                    </SyntaxHighlighter>
                                );
                            })()
                        ) : (
                            <>
                                <div>{t.description}</div>
                                <Stack horizontal tokens={{ childrenGap: 5 }} className={styles.verticalStack}>
                                    {t.props &&
                                        Object.keys(t.props).map((k: any) => (
                                            <span className={styles.tProp} key={k}>
                                                {k}: {JSON.stringify(t.props?.[k])}
                                            </span>
                                        ))}
                                </Stack>
                            </>
                        )}
                    </li>
                ))}
            </ul>

            {/* ✅ Always render token usage at the end if available */}
            {usage && <TokenUsageCard usage={usage} />}
        </>
    );
};
